import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import { useApolloClient } from "@apollo/client";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { useLocale } from "../context/LanguageContext";
import { OrganizationSettingsPage as Page } from "../features/organization-settings";

/** Channel name shared between parent tab and OAuth tab */
const SLACK_OAUTH_CHANNEL = "slack-oauth-callback";

type ToastState = {
  type: "success" | "error";
  message: string;
} | null;

export function OrganizationSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useApolloClient();
  const { t } = useLocale();
  const [toast, setToast] = useState<ToastState>(null);

  // ── Listen for BroadcastChannel messages from the OAuth tab ────────────────
  const handleOAuthResult = useCallback(
    (data: { status: string; reason?: string }) => {
      console.log("[SlackOAuth] Parent received OAuth result:", data);

      if (data.status === "connected") {
        setToast({ type: "success", message: t("settings.slackConnected") });
        void client.refetchQueries({ include: ["ChatIntegration"] });
      } else if (data.status === "pending") {
        setToast({ type: "success", message: t("settings.slackPending") });
        void client.refetchQueries({ include: ["ChatIntegration"] });
      } else if (data.status === "error") {
        setToast({
          type: "error",
          message: data.reason
            ? t("settings.slackFailed", { reason: data.reason })
            : t("settings.slackFailedGeneric"),
        });
      }
    },
    [client],
  );

  useEffect(() => {
    const bc = new BroadcastChannel(SLACK_OAUTH_CHANNEL);
    bc.onmessage = (event: MessageEvent<{ status: string; reason?: string }>) => {
      handleOAuthResult(event.data);
    };
    return () => bc.close();
  }, [handleOAuthResult]);

  // ── Handle ?slack= param — this runs in the OAuth redirect tab ─────────────
  useEffect(() => {
    const slackStatus = searchParams.get("slack");
    if (!slackStatus) return;

    console.log("[SlackOAuth] Detected ?slack= param:", { slackStatus });

    // Broadcast result to parent tab via BroadcastChannel
    const bc = new BroadcastChannel(SLACK_OAUTH_CHANNEL);
    bc.postMessage({
      status: slackStatus,
      reason: searchParams.get("reason") ?? undefined,
    });
    bc.close();

    console.log("[SlackOAuth] Broadcasted result to parent, closing tab…");

    // Close this tab — we're done here
    window.close();

    // If window.close() was blocked (shouldn't be for user-initiated tabs, but just in case),
    // the page will render the normal settings page with the toast below as fallback
    if (slackStatus === "connected") {
      setToast({ type: "success", message: t("settings.slackSuccess") });
    } else if (slackStatus === "pending") {
      setToast({ type: "success", message: t("settings.slackApprovalPending") });
    } else if (slackStatus === "error") {
      const reason = searchParams.get("reason");
      setToast({
        type: "error",
        message: reason
          ? t("settings.slackFailed", { reason })
          : t("settings.slackFailedGeneric"),
      });
    }

    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, client]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  return (
    <div>
      {toast && (
        <div className="mx-auto max-w-2xl px-6 pt-6">
          <div
            className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
              toast.type === "success"
                ? "border-success/30 bg-success/10 text-success"
                : "border-destructive/30 bg-destructive/10 text-destructive"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0" />
            )}
            {toast.message}
          </div>
        </div>
      )}
      <Page />
    </div>
  );
}
